import React from "react";
import Dialog from "rc-dialog";
import moment from "moment";
import axios from "axios";
import classNames from "classnames";
import Select from "react-select-new";

import EventTemplate from "./eventTemplate.js";
import EventTemplateELearning from "./eventTemplateElearning.js";
import { Tooltip } from "../../../components/Tooltip";
import { hasStaffRole } from "../../../utils/auth.jsx";

const scoreTypesByEventType = {
    practice: {
        good: "Goed",
        sufficient: "Voldoende",
        to_be_improved: "Voor verbetering vatbaar",
        insufficient: "Onvoldoende"
    },
    knowledge: {
        correct: "Correct",
        incorrect: "Incorrect"
    },
    "knowledge-exam": {
        achieved: "Behaald",
        not_achieved: "Niet behaald"
    }
};

function BulkOptionsSelect({ eventType, selected, onChange, className, disabled, ...props }) {
    const options = Object.entries(scoreTypesByEventType[eventType]).map(([value, label]) => ({ value, label }));
    const value = options.find(({ value }) => value === selected) || null;

    return (
        <Select
            isDisabled={disabled}
            name="result"
            value={value}
            className={[className, "react-select-new"].filter(Boolean).join(" ")}
            classNamePrefix="react-select"
            onChange={onChange}
            // controlShouldRenderValue={true}
            options={options}
            isClearable
            backspaceRemovesValue
            // placeholder={props?.trainers?.length > 0 ? "" : ""}
            // noOptionsMessage={() => "Geen resultaten"}
            placeholder="Selecteer resultaat"
            {...props}
        />
    );
}

/**
 * Displays details / notes related to bulk results based on the given properties of
 * a student.
 * @param {Boolean} hasResults
 * @param {Boolean} isExempt
 * @param props
 * @returns {JSX.Element|null}
 */
function BulkStudentDetailsToolTip({ hasResults = false, isExempt = false, ...props }) {
    if (!hasResults && !isExempt) {
        return null;
    }

    const id = [];
    if (hasResults) {
        id.push("has-results");
    }
    if (isExempt) {
        id.push("is-exempt");
    }

    return (
        <Tooltip
            identifier={id.join("-")}
            {...props}
            tooltip={
                <>
                    {isExempt && (
                        <>
                            <p>
                                <strong>Cursist heeft vrijstellingen</strong>
                                <br />
                                Deze cursist is (deels) vrijgesteld. Er zullen geen resultaten voor toetsingen in vrijgestelde eindtermen worden aangemaakt.
                            </p>
                            <p>Na afloop van het toetsmoment zullen de resultaten voor de vrijgestelde toetsingen worden aangemaakt.</p>
                        </>
                    )}
                    {hasResults && !isExempt && (
                        <p>
                            <strong>Cursist heeft al resultaten</strong>
                            <br />
                            Er zijn al resultaten ingestuurd voor deze cursist. Als hier een resultaat wordt opgegeven zal dit de bestaande resultaten overschrijven.
                        </p>
                    )}
                </>
            }
        />
    );
}

/**
 * Shows a confirm bulk results dialog.
 * @param {Object} results
 * @param {Object} event
 * @param {Function} onConfirm
 * @param {Function} onClose
 * @param props
 * @returns {JSX.Element}
 */
function ConfirmBulkResultsDialog({ results, event, onConfirm, onClose, ...props }) {
    const handleSubmit = () => {
        if (!Object.keys(results).length) {
            return;
        }
        return onConfirm(results);
    };

    return (
        <Dialog title="Resultaten insturen" onClose={onClose} {...props}>
            <p>Het insturen van resultaten heeft tot gevolg dat&hellip;</p>
            <ul>
                <li>Eventuele bestaande resultaten worden overschreven.</li>
                <li>niets van het toetsmoment kan nog worden gewijzigd.</li>
                <li>NIBHV verzocht zal moeten worden om resultaten voor eventuele extra cursisten te verwerken.</li>
            </ul>

            <h3>De volgende resultaten zullen worden ingestuurd</h3>
            <table className="table table-hover mt-2 mb-4">
                <thead>
                    <tr>
                        <th scope="col">NIBHV nummer</th>
                        <th scope="col">Naam</th>
                        <th scope="col" />
                        <th scope="col">Resultaat</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(results).map(([studentUUID, scoreType]) => {
                        const student = event.event_roles[0].students.find((student) => student.uuid === studentUUID);
                        return (
                            <tr key={studentUUID}>
                                <td>{student.nibhv_number}</td>
                                <td>{student.full_name}</td>
                                <td>{<BulkStudentDetailsToolTip hasResults={student.has_results} isExempt={student.is_exempt} />}</td>
                                <td>{scoreTypesByEventType[event.type][scoreType]}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <p className="alert alert-info">
                Het verwerken van de resultaten die worden ingestuurd kan enige tijd duren en is afhankelijk van de drukte binnen NIBHV Competent. De meeste resultaten
                zijn binnen 1 uur verwerkt.
            </p>

            <p>Weet je zeker dat je deze resultaten wilt insturen?</p>
            <p className="d-flex justify-content-between">
                <button className="btn btn-secondary" onClick={onClose}>
                    Annuleren
                </button>
                <button className="btn btn-primary" onClick={handleSubmit}>
                    Ja, stuur deze resultaten in
                </button>
            </p>
        </Dialog>
    );
}

export default class MeasurementMoment extends React.Component {
    constructor(props) {
        super(props);

        this.timer = null;

        this.state = {
            eventUUID: false,
            eventDetails: false,
            loading: false,
            eventValues: false,
            bulkResults: {},
            confirmBulkResults: false,
            confirmSaveWithBulkResults: false,
            showWarningModal: false
        };
    }

    getEventData() {
        if (!this.props.eventUUID) {
            this.setState({ loading: false });
            return;
        }

        axios
            .get(`/v1/courses/${this.props.course.uuid}/events/${this.props.eventUUID}/`)
            .then((event) => {
                this.setState({
                    eventUUID: this.props.eventUUID,
                    eventDetails: event.data,
                    loading: false
                });
            })
            .catch((error) => {
                console.log(error);
            });
    }

    updateEventData = async (values) => {
        // Check if we have events that have this specific event as a prerequisite
        // If so, update the end datetime of that event to the startdatetime of this one
        let eventTemplateUUID = this.state.eventDetails.template;
        let eventTemplate = this.props.eventTemplates.find((et) => et.uuid === eventTemplateUUID);

        const courseEventsURL = `/v1/courses/${this.props.course.uuid}/events/`;

        // Check if we have this event template
        if (eventTemplate) {
            let prerequisites = eventTemplate.prerequisites;
            let prereqTemplates = this.props.eventTemplates.filter((et) => prerequisites.includes(et.uuid));

            // Check if we found a template that is a prerequisite
            if (prereqTemplates) {
                // We found other templates that this event relies upon, find attached events
                for (const eventTemplate of prereqTemplates) {
                    if (this.props && this.props.events) {
                        let prereqEvents = this.props.events.filter((event) => event.template === eventTemplate.uuid);

                        for (const event of prereqEvents) {
                            if (event.is_editable) {
                                try {
                                    await axios.patch(`${courseEventsURL}${event.uuid}/`, {
                                        end_datetime: values.start_datetime
                                    });
                                } catch (error) {
                                    console.error(`Failed to update prerequisite event: ${event.uuid}`, error);
                                    throw error;
                                }
                            }
                        }
                    }
                }
            }
        }

        try {
            await axios.patch(`${courseEventsURL}${this.state.eventUUID}/`, {
                start_datetime: values.start_datetime,
                end_datetime: values.end_datetime,
                location: values.location,
                event_roles:
                    values.event_roles && values.event_roles.length > 0
                        ? [
                            {
                                ...values.event_roles[0],
                                instructors: values.event_roles[0].instructors || []
                            }
                        ]
                        : []
            });
        } catch (error) {
            console.error("Failed to update the event", error);
            const errorMessage = this.extractErrorMessage(error.response?.data);
            throw new Error(errorMessage);
        }
    };

    getEventDataOnly = () => {
        axios.get(`/v1/courses/${this.props.course.uuid}/events/${this.props.eventUUID}/`).then((events) => {
            this.setState({
                eventDetails: events.data
            });
        });
    };

    updateStudentAttendance = (studentUUID, eventUUID, eventRoleUUID, willAttend) => {
        axios
            .patch(`/v1/courses/${this.props.course.uuid}/events/${eventUUID}/eventroles/${eventRoleUUID}/students/${studentUUID}/`, {
                will_attend: willAttend
            })
            .then((response) => {
                const bulkResults = { ...this.state.bulkResults, [studentUUID]: "" };
                this.setState({ bulkResults });
                this.getEventDataOnly();
                this.props.parentGetEvents();
            })
            .catch((error) => {
                console.log(error);
            });
    };

    passValuesToParent = (values) => {
        this.setState({
            eventValues: {
                ...values
            }
        });
    };

    handleSaveButton = async (confirmed) => {
        if (!confirmed && Object.keys(this.state.bulkResults).length) {
            // There are bulk results in the state, ensure the user intended to save the
            // event details instead of sending in the bulk results:
            return this.setState({ confirmSaveWithBulkResults: true });
        }
        if (this.state.eventValues !== false) {
            try {
                await this.updateEventData(this.state.eventValues);
                this.props.passValuesToParent(this.state.eventValues);

                this.setState({
                    bulkResults: {},
                    confirmBulkResults: false,
                    confirmSaveWithBulkResults: false,
                    saveError: ""
                });

                this.props.closeStudentDialog();
            } catch (error) {
                console.error("Failed to save event:", error);
                this.setState({ saveError: error.message });
            }
        }
    };

    extractErrorMessage = (errorData) => {
        if (!errorData) return "";

        if (Array.isArray(errorData.event_roles)) {
            for (const role of errorData.event_roles) {
                if (Array.isArray(role.instructors)) {
                    for (const instructor of role.instructors) {
                        if (Array.isArray(instructor.instructor) && instructor.instructor.includes("User is not active")) {
                            return "Je kunt geen inactieve instructeur toewijzen.";
                        }
                    }
                }
            }
        }

        return "Failed to save event. Please try again.";
    };

    saveBulkResults = () => {
        // close the confirm bulk results dialog and start saving the results:
        this.setState({ confirmBulkResults: false, savingBulkResults: true });
        const event = this.state.eventDetails;
        const bulkResults = Object.entries(this.state.bulkResults).map(([user, score_type]) => ({ user, score_type }));
        // console.info({event, bulkResults});
        return axios.post(`/v1/courses/${event.course}/events/${event.uuid}/bulk-results/`, bulkResults).finally(() => {
            this.setState({
                // reset the bulk results overlay:
                bulkResults: {},
                savingBulkResults: false,
                confirmBulkResults: false,
                confirmSaveWithBulkResults: false,
                // and set loading to true because we are going to fetch event data:
                loading: true
            });
            // update event data:
            this.getEventData();
        });
    };

    componentDidUpdate = (prevProps) => {
        if (!this.state.loading && this.props.eventUUID !== false && this.state.eventUUID !== this.props.eventUUID) {
            this.getEventData();
        }
        if (prevProps.eventUUID !== this.props.eventUUID) {
            this.setState({
                savingBulkResults: false,
                studentsMessage: null,
                bulkResults: {},
                confirmBulkResults: false
            });
        }
    };

    checkDateAgainstEvents = (currentEvent, events) => {
        if (!currentEvent.start_datetime || events.length === 0) return false;

        const parsedDate = new Date(currentEvent.start_datetime);

        for (let i = 0; i < events.length; i++) {
            if (!events[i].start_datetime) continue;

            const parsedEventDate = new Date(events[i].start_datetime);
            const differenceInDays = Math.abs((parsedEventDate - parsedDate) / (1000 * 3600 * 24));

            if (differenceInDays >= 93) {
                return true;
            }
        }

        return false;
    };

    render = () => {
        let details = <p>Toetsmomenten ophalen...</p>;
        let studentRows = [];

        const busy = this.state.savingBulkResults || this.state.loading;

        if (!this.state.loading && this.state.eventDetails) {
            let currentEvent = this.state.eventDetails;
            let currentTemplate = this.props.eventTemplates.find((template) => template.uuid === currentEvent.template);

            const now = moment();
            const bulkResultsPossible = currentEvent && Object.keys(scoreTypesByEventType).includes(currentEvent.type);
            const bulkResultsAllowedFrom = moment(currentEvent.start_datetime).startOf("day");
            const bulkResultsAllowed = bulkResultsAllowedFrom.isSameOrBefore(now.startOf("day"));
            const showBulkResultsControls = bulkResultsPossible && bulkResultsAllowed;

            // console.info({
            //     now,
            //     bulkResultsPossible,
            //     bulkResultsAllowedFrom,
            //     bulkResultsAllowed,
            //     showBulkResultsControls,
            // });

            if (currentEvent && currentEvent.event_roles && currentEvent.event_roles[0] && currentEvent.event_roles[0].students) {
                currentEvent.event_roles[0].students.forEach((student) => {
                    const { will_attend: willAttend, attended: hasAttended, is_exempt: isExempt } = student;

                    let attendButton;
                    if (currentEvent.type === "elearning") {
                        // Presence is not given for elearning events
                        attendButton =
                            {
                                new: "Nog niet begonnen",
                                active: "Nog bezig",
                                achieved: "Behaald",
                                "not-achieved": "Nog bezig"
                            }[student.elearning_status] || "";
                    } else if (student.has_results || !currentEvent.is_editable) {
                        attendButton = (
                            <i
                                className={classNames("fa", {
                                    "fa-check": hasAttended,
                                    "fa-times": !hasAttended
                                })}
                            />
                        );
                    } else {
                        attendButton = (
                            <i
                                onClick={() => this.updateStudentAttendance(student.uuid, currentEvent.uuid, currentEvent.event_roles[0].uuid, !willAttend)}
                                className={classNames("fa color-blue", {
                                    "fa-toggle-on": willAttend,
                                    "fa-toggle-off": !willAttend
                                })}
                            />
                        );
                    }

                    studentRows.push(
                        <tr key={`studentrow-${student.uuid}`}>
                            {/*  className={ classNames({ 'inactive' : !willAttend })} */}
                            <td onClick={() => window.open(`/students/courses/${student.uuid}/${currentEvent.course}`)}>{student.full_name}</td>
                            <td>{attendButton}</td>
                            {showBulkResultsControls && (
                                <>
                                    <td>
                                        <BulkStudentDetailsToolTip hasResults={student.has_results} isExempt={student.is_exempt} containerClass="ml-0 mr-2" />
                                    </td>
                                    <td>
                                        {!student.is_exempt ? (
                                            <div className="d-flex align-items-center">
                                                <BulkOptionsSelect
                                                    disabled={["finished", "cancelled"].includes(this.props.course.state)}
                                                    className="score-type-select"
                                                    eventType={currentEvent.type}
                                                    selected={this.state.bulkResults[student.uuid] || ""}
                                                    onChange={(event) => {
                                                        if (event === null) {
                                                            delete this.state.bulkResults[student.uuid];
                                                            return this.setState({ bulkResults: this.state.bulkResults });
                                                        }
                                                        const { bulkResults, ...state } = this.state;
                                                        this.setState({
                                                            bulkResults: Object.assign({}, bulkResults, { [student.uuid]: event.value }),
                                                            ...state
                                                        });
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            "Vrijgesteld"
                                        )}
                                    </td>
                                </>
                            )}
                        </tr>
                    );
                });
            }

            if (studentRows.length === 0) {
                studentRows.push(
                    <tr key={"studentrow-nostudents"}>
                        <td className={"overview-redux__error-message"}>-</td>
                        <td className={"overview-redux__error-message"}>-</td>
                    </tr>
                );
            }

            let eventTemplate;
            if (currentEvent.type === "elearning") {
                eventTemplate = (
                    <EventTemplateELearning
                        {...this.props}
                        key={"eventitemdetails-" + currentTemplate.uuid}
                        courseUUID={currentEvent.course}
                        trainerUUID={currentEvent.trainer}
                        templateUUID={currentTemplate.uuid}
                        competenceProfileUUID={currentEvent.competence_profile}
                        templateDetails={currentTemplate}
                        eventDetails={currentEvent}
                        editable={this.props.editable}
                        allEventDetails={this.props.events}
                        passValuesToParent={() => {}}
                        splittedEvents={this.props.splittedEvents || false}
                        hideTitle={true}
                    />
                );
            } else {
                eventTemplate = (
                    <EventTemplate
                        {...this.props}
                        key={"eventitemdetails-" + currentTemplate.uuid}
                        courseUUID={currentEvent.course}
                        trainerUUID={currentEvent.trainer}
                        templateUUID={currentTemplate.uuid}
                        competenceProfileUUID={currentEvent.competence_profile}
                        templateDetails={currentTemplate}
                        eventDetails={currentEvent}
                        passValuesToParent={this.passValuesToParent}
                        editable={this.props.editable}
                        splittedEvents={this.props.splittedEvents || false}
                        hideTitle={true}
                    />
                );
            }
            const roleCount = currentEvent.event_roles.length;
            const anyRolesAssessed = currentEvent.event_roles.some((role) => role.assessed);

            const bulkValues = Object.values(this.state.bulkResults);
            const bulkResultAllValue = bulkValues.every((v) => v === bulkValues[0]) ? bulkValues[0] : "";

            const isNIBHVUser = hasStaffRole(this.props.authentication?.user?.roles || []);

            details = (
                <>
                    {this.state.saveError && (
                        <div className="form-error alert alert-danger">
                            <span>{this.state.saveError}</span>
                        </div>
                    )}
                    <div className="mt-3">{eventTemplate}</div>

                    {isNIBHVUser || this.props.editable ? (
                        <p className="d-flex justify-content-end">
                            <button
                                className="btn btn-primary"
                                disabled={busy || !this.state.eventValues}
                                onClick={() => {
                                    const filteredEvents = this.props.events
                                        ?.filter((e) => e.uuid !== this.state.eventValues?.uuid)
                                        .filter((e) => e.type !== "elearning");
                                    if (
                                        this.props.competenceProfile?.warn_if_events_are_too_far_apart &&
                                        this.checkDateAgainstEvents(this.state.eventValues, filteredEvents)
                                    ) {
                                        this.setState({ showWarningModal: true });
                                        return;
                                    } else {
                                        this.handleSaveButton();
                                    }
                                }}
                            >
                                Toetsmoment opslaan
                            </button>
                        </p>
                    ) : null}

                    {currentEvent.type !== "elearning" && (
                        <>
                            <h2>Beoordelingen</h2>
                            {anyRolesAssessed ? (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            {roleCount > 1 && <th>Rol</th>}
                                            <th>Beoordeeld</th>
                                            <th>Door</th>
                                            <th>Datum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentEvent.event_roles.map((role, i) => (
                                            <tr key={i}>
                                                {roleCount > 1 && <th>{role.name || i + 1}</th>}
                                                <td>{role.assessed ? "Ja" : "Nee"}</td>
                                                <td>{role.assessed_by ? role.assessed_by.full_name : "-"}</td>
                                                <td>{role.assessed_on ? moment(role.assessed_on).format("YYYY-MM-DD HH:mm") : "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            {roleCount > 1 && <th>Rol</th>}
                                            <th>Beoordeeld</th>
                                            <th>Door</th>
                                            <th>Datum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            {roleCount > 1 && <td className={"overview-redux__error-message"}>-</td>}
                                            <td className={"overview-redux__error-message"}>-</td>
                                            <td className={"overview-redux__error-message"}>-</td>
                                            <td className={"overview-redux__error-message"}>-</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}

                    <h2>Cursisten</h2>
                    {currentEvent.has_results && (
                        <div className="alert alert-info">
                            Er zijn al resultaten ingestuurd voor dit toetsmoment. Weet je zeker dat je deze resultaten wilt overschrijven? Mogelijk heeft dit
                            consequenties voor behaalde certificeringen.
                        </div>
                    )}
                    {bulkResultsPossible && !bulkResultsAllowed && (
                        <div className="alert alert-info">
                            Het is pas mogelijk om resultaten in te sturen vanaf de dag dat het toetsmoment begint:{" "}
                            <time dateTime={bulkResultsAllowedFrom.toISOString(false)} className="text-nowrap">
                                ({bulkResultsAllowedFrom.format("D-M-YYYY")})
                            </time>
                        </div>
                    )}
                    {this.state.studentsMessage && (
                        <div className={classNames("alert", `alert-${this.state.studentsMessage.type || "info"}`)}>{this.state.studentsMessage.text}</div>
                    )}
                    <table className="table table-hover table-clickable">
                        <thead>
                            <tr>
                                <th>Naam</th>
                                <th width="180">{currentEvent.type !== "elearning" ? (currentEvent.is_editable ? "Aanwezig" : "Was aanwezig") : "Status"}</th>
                                {showBulkResultsControls && (
                                    <>
                                        <th />
                                        <th width="240">
                                            <BulkOptionsSelect
                                                className="score-type-select"
                                                eventType={currentEvent.type}
                                                selected={bulkResultAllValue}
                                                disabled={["finished", "cancelled"].includes(this.props.course.state)}
                                                onChange={(event) => {
                                                    if (!event) {
                                                        return this.setState({ bulkResults: {} });
                                                    }

                                                    const isStudentValid = (student) => {
                                                        if (student.is_exempt) return false;
                                                        if (!currentEvent.is_editable && !student.attended) return false;
                                                        if (currentEvent.is_editable && !student.will_attend) return false;
                                                        return true;
                                                    };

                                                    const bulkResults =
                                                        currentEvent.event_roles[0]?.students?.reduce((results, student) => {
                                                            if (isStudentValid(student)) {
                                                                results[student.uuid] = event.value;
                                                            }
                                                            return results;
                                                        }, {}) || {};

                                                    const bulkResultAll = event.value;

                                                    this.setState({ bulkResults, bulkResultAll });
                                                }}
                                            />
                                        </th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tfoot>
                            {showBulkResultsControls && (
                                <tr>
                                    <td colSpan="4" className="text-right">
                                        <button
                                            className="btn btn-primary"
                                            disabled={busy || !bulkValues.length}
                                            onClick={() => this.setState({ confirmBulkResults: true, savingBulkResults: false })}
                                        >
                                            Resultaten insturen
                                        </button>
                                    </td>
                                </tr>
                            )}
                            <tr className={classNames({ noborder: showBulkResultsControls })}>
                                <td colSpan="4">
                                    <button className="btn btn-secondary" disabled={this.state.savingBulkResults} onClick={() => this.props.closeStudentDialog()}>
                                        {this.state.savingBulkResults ? <>Bezig met opslaan <i className="fa fa-spinner fa-spin" /></> : "Sluit venster"}
                                    </button>
                                </td>
                            </tr>
                        </tfoot>
                        <tbody>{studentRows}</tbody>
                    </table>
                    <Dialog
                        title="Weet je het zeker?"
                        visible={this.state.confirmSaveWithBulkResults}
                        onClose={() => this.setState({ confirmSaveWithBulkResults: false })}
                    >
                        <p>Er zijn cursist resultaten ingevuld. Wil je deze niet eerst insturen?</p>
                        <p className="d-flex justify-content-between">
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    // switch to confirming sending in bulk results:
                                    this.setState({
                                        confirmSaveWithBulkResults: false,
                                        confirmBulkResults: true,
                                        savingBulkResults: false
                                    });
                                }}
                            >
                                Ja, die wilde ik insturen
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    // call the save function with an additional confirmed flag:
                                    this.handleSaveButton(true);
                                }}
                            >
                                Nee, sla alleen het toetsmoment op
                            </button>
                        </p>
                    </Dialog>

                    {showBulkResultsControls && (
                        <ConfirmBulkResultsDialog
                            results={this.state.bulkResults}
                            event={currentEvent}
                            visible={this.state.confirmBulkResults}
                            onConfirm={() => {
                                this.saveBulkResults().then(
                                    (response) => {
                                        this.passValuesToParent({
                                            uuid: currentEvent.uuid,
                                            has_results: true
                                        });
                                        this.setState({
                                            studentsMessage: {
                                                type: "success",
                                                text: "De resultaten zijn opgeslagen! Meestal zijn de resultaten binnen een uur verwerkt."
                                            }
                                        });
                                    },
                                    (error) => {
                                        console.warn(error);
                                        this.setState({
                                            studentsMessage: {
                                                type: "warning",
                                                text: "Er is een fout opgetreden bij het insturen van de resultaten"
                                            }
                                        });
                                    }
                                );
                            }}
                            onClose={() => this.setState({ confirmBulkResults: false })}
                        />
                    )}
                    <Dialog
                        noClose
                        className="testmoments-date-warning-modal"
                        visible={this.state.showWarningModal}
                        animation="fade"
                        footer={[
                            <>
                                <button
                                    className="btn btn-primary no-sideflap"
                                    onClick={() => {
                                        this.setState({ showWarningModal: false });
                                    }}
                                >
                                    Terug
                                </button>
                                <button
                                    className="btn btn-primary no-sideflap"
                                    onClick={() => {
                                        this.setState({ showWarningModal: false }, () => this.handleSaveButton());
                                    }}
                                >
                                    Ik begrijp het
                                </button>
                            </>
                        ]}
                    >
                        <p className="paragraph">
                            Om in aanmerking te komen voor een compleet bhv basisdiploma of herhalingscertificaat, mag de periode tussen toetsmomenten niet langer zijn
                            dan 93 dagen. Certificeren van de afzonderlijke onderdelen is uiteraard wel mogelijk, maar enkel middels deelcertificaten.
                        </p>
                    </Dialog>
                </>
            );
        }

        return (
            <>
                <Dialog
                    title={this.state.loading ? "Toetsmoment ophalen..." : this.state.eventDetails.name}
                    closable={!busy}
                    maskClosable={!busy}
                    onClose={() => this.props.closeStudentDialog()}
                    visible={this.props.visible}
                >
                    {details}
                </Dialog>
            </>
        );
    };
}
